import { Controller } from "@hotwired/stimulus"

// Per-selection sessionStorage key so Compare([27,26]) and
// Compare([27,25,24]) don't share accordion state. IntersectionObserver
// rehydration prevents 3-section reopen from firing 3 parallel fetches.
const STORAGE_KEY_PREFIX = "ec_sections_open"

export default class extends Controller {
  static targets = ["section"]
  static values  = {
    eventIds: { type: String, default: "" }
  }

  get _storageKey() {
    const ids = (this.eventIdsValue || "")
      .split(",")
      .flatMap(s => s.split("-"))
      .map(s => s.trim())
      .filter(Boolean)
      .sort()
      .join("-")
    return `${STORAGE_KEY_PREFIX}_${ids || "default"}`
  }

  connect() {
    const stored = this._loadOpenSet()
    if (stored.size === 0) return

    if (typeof IntersectionObserver === "undefined") {
      stored.forEach(sectionId => this._rehydrateSection(sectionId))
      return
    }

    this._observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return
        const sectionId = entry.target.dataset.sectionId
        if (sectionId && stored.has(sectionId)) {
          this._rehydrateSection(sectionId)
        }
        this._observer.unobserve(entry.target)
      })
    }, { rootMargin: "100px" })

    this.sectionTargets.forEach(el => this._observer.observe(el))
  }

  disconnect() {
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
  }

  loadFrame(event) {
    const el = event.target
    const frameId = el?.dataset?.frame
    const src     = el?.dataset?.src
    if (!frameId || !src) return

    const frame = document.getElementById(frameId)
    if (!frame) return
    if (frame.getAttribute("src")) return

    frame.setAttribute("src", src)
  }

  markOpen(event) {
    const sectionId = event.target?.dataset?.sectionId
    if (!sectionId) return
    const set = this._loadOpenSet()
    set.add(sectionId)
    this._saveOpenSet(set)
  }

  markClosed(event) {
    const sectionId = event.target?.dataset?.sectionId
    if (!sectionId) return
    const set = this._loadOpenSet()
    set.delete(sectionId)
    this._saveOpenSet(set)
  }

  _rehydrateSection(sectionId) {
    const collapse = this.element.querySelector(`[data-section-id="${sectionId}"]`)
    if (!collapse) return

    const bs = (window.bootstrap && window.bootstrap.Collapse)
               ? new window.bootstrap.Collapse(collapse, { toggle: false })
               : null
    if (bs) bs.show()
  }

  _loadOpenSet() {
    try {
      const raw = sessionStorage.getItem(this._storageKey)
      return new Set(raw ? JSON.parse(raw) : [])
    } catch (e) {
      return new Set()
    }
  }

  _saveOpenSet(set) {
    try {
      sessionStorage.setItem(this._storageKey, JSON.stringify([...set]))
    } catch (e) {
      // sessionStorage disabled (Safari Private Mode, iOS Lockdown) — no persistence
    }
  }
}
